import React, { useState, useEffect } from 'react';
import {
    X, User, Mail, Calendar, LogOut, ChevronRight, ChevronLeft,
    Shield, HelpCircle, Settings, Bell, Database, Lock
} from 'lucide-react';
import { updateUserDisplayName, sendPasswordReset } from '../services/firebase';

const SECTIONS = {
    main: 'main',
    account: 'account',
    privacy: 'privacy',
    help: 'help'
};

const ProfileModal = ({ showProfileModal, setShowProfileModal, user, onSignOut }) => {
    const [section, setSection] = useState(SECTIONS.main);
    const [displayName, setDisplayName] = useState('');
    const [savingName, setSavingName] = useState(false);
    const [profileMessage, setProfileMessage] = useState(null);
    const [resetSent, setResetSent] = useState(false);

    useEffect(() => {
        if (showProfileModal) {
            setDisplayName(user?.displayName || '');
            setSection(SECTIONS.main);
            setProfileMessage(null);
            setResetSent(false);
        }
    }, [showProfileModal, user?.displayName]);

    if (!showProfileModal) return null;

    const handleSignOut = () => {
        setShowProfileModal(false);
        onSignOut();
    };

    const handleSaveDisplayName = async () => {
        setSavingName(true);
        setProfileMessage(null);
        const res = await updateUserDisplayName(displayName.trim());
        setSavingName(false);
        setProfileMessage(res.success ? 'Display name updated.' : res.error || 'Could not update name.');
    };

    const handlePasswordReset = async () => {
        if (!user?.email) {
            setProfileMessage('No email on this account.');
            return;
        }
        const res = await sendPasswordReset(user.email);
        if (res.success) {
            setResetSent(true);
            setProfileMessage('Password reset email sent — check your inbox.');
        } else {
            setProfileMessage(res.error || 'Could not send reset email.');
        }
    };

    const backButton = section !== SECTIONS.main && (
        <button
            type="button"
            onClick={() => setSection(SECTIONS.main)}
            className="flex items-center text-sm text-gray-600 hover:text-gray-900 mb-4"
        >
            <ChevronLeft className="w-4 h-4 mr-1" />
            Back
        </button>
    );

    const menuRow = (id, Icon, label, description) => (
        <button
            type="button"
            key={id}
            onClick={() => setSection(id)}
            className="w-full flex items-center justify-between px-3 py-3 hover:bg-gray-100 rounded-xl transition-colors text-left"
        >
            <div className="flex items-center gap-3 min-w-0">
                <div className="w-9 h-9 rounded-lg bg-gray-100 flex items-center justify-center shrink-0">
                    <Icon className="w-4 h-4 text-gray-600" />
                </div>
                <div className="min-w-0">
                    <p className="text-sm font-medium text-gray-900">{label}</p>
                    <p className="text-xs text-gray-500">{description}</p>
                </div>
            </div>
            <ChevronRight className="w-4 h-4 text-gray-400 shrink-0 ml-2" />
        </button>
    );

    return (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
            <div className="bg-white rounded-2xl shadow-xl max-w-md w-full max-h-[90vh] overflow-hidden flex flex-col">
                <div className="flex items-center justify-between p-6 border-b border-gray-100">
                    <h2 className="text-xl font-bold text-gray-900">
                        {section === SECTIONS.main && 'Profile'}
                        {section === SECTIONS.account && 'Account settings'}
                        {section === SECTIONS.privacy && 'Privacy'}
                        {section === SECTIONS.help && 'Help & support'}
                    </h2>
                    <button
                        type="button"
                        onClick={() => setShowProfileModal(false)}
                        className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                    >
                        <X className="w-5 h-5 text-gray-500" />
                    </button>
                </div>

                <div className="p-6 overflow-y-auto flex-1">
                    {section === SECTIONS.main && (
                        <div className="space-y-4">
                            <div className="flex items-center space-x-4">
                                <div className="w-16 h-16 bg-gradient-to-br from-blue-500 to-purple-600 rounded-full flex items-center justify-center shrink-0">
                                    {user?.photoURL ? (
                                        <img
                                            src={user.photoURL}
                                            alt=""
                                            className="w-16 h-16 rounded-full object-cover"
                                        />
                                    ) : (
                                        <User className="w-8 h-8 text-white" />
                                    )}
                                </div>
                                <div className="min-w-0">
                                    <h3 className="font-semibold text-gray-900 truncate">
                                        {user?.displayName || 'User'}
                                    </h3>
                                    <div className="flex items-center text-gray-600 text-sm mt-1 truncate">
                                        <Mail className="w-4 h-4 mr-1 shrink-0" />
                                        <span className="truncate">{user?.email || 'No email'}</span>
                                    </div>
                                    {user?.metadata?.creationTime && (
                                        <div className="flex items-center text-gray-500 text-xs mt-1">
                                            <Calendar className="w-3 h-3 mr-1" />
                                            Joined {new Date(user.metadata.creationTime).toLocaleDateString()}
                                        </div>
                                    )}
                                </div>
                            </div>

                            <div className="border-t pt-2 space-y-1">
                                {menuRow(SECTIONS.account, Settings, 'Account settings', 'Name, email, password')}
                                {menuRow(SECTIONS.privacy, Shield, 'Privacy settings', 'Your data and security')}
                                {menuRow(SECTIONS.help, HelpCircle, 'Help & support', 'Tips and contact')}
                            </div>
                        </div>
                    )}

                    {section === SECTIONS.account && (
                        <div>
                            {backButton}
                            <div className="space-y-5">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        Display name
                                    </label>
                                    <input
                                        type="text"
                                        value={displayName}
                                        onChange={(e) => setDisplayName(e.target.value)}
                                        className="w-full px-3 py-2 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-purple-500 text-sm"
                                        placeholder="Your name"
                                    />
                                    <button
                                        type="button"
                                        disabled={savingName || !displayName.trim()}
                                        onClick={handleSaveDisplayName}
                                        className="mt-2 px-4 py-2 text-sm font-medium rounded-xl bg-gradient-to-r from-purple-600 to-violet-600 text-white disabled:opacity-50"
                                    >
                                        {savingName ? 'Saving…' : 'Save name'}
                                    </button>
                                </div>

                                <div className="border-t pt-4">
                                    <p className="text-sm font-medium text-gray-900 mb-1">Email</p>
                                    <p className="text-sm text-gray-600">{user?.email || '—'}</p>
                                    <p className="text-xs text-gray-500 mt-1">
                                        Email is managed by your sign-in provider and cannot be changed here.
                                    </p>
                                </div>

                                <div className="border-t pt-4">
                                    <p className="text-sm font-medium text-gray-900 mb-2 flex items-center gap-2">
                                        <Lock className="w-4 h-4" />
                                        Password
                                    </p>
                                    <button
                                        type="button"
                                        onClick={handlePasswordReset}
                                        disabled={resetSent}
                                        className="text-sm text-purple-700 hover:text-purple-900 font-medium disabled:text-gray-400"
                                    >
                                        {resetSent ? 'Reset email sent' : 'Send password reset email'}
                                    </button>
                                </div>

                                <div className="border-t pt-4">
                                    <p className="text-sm font-medium text-gray-900 mb-2 flex items-center gap-2">
                                        <Bell className="w-4 h-4" />
                                        Notifications
                                    </p>
                                    <p className="text-sm text-gray-600">
                                        Email and in-app notifications for imports and processing will be available in a
                                        future update. Your account is active with no pending alerts.
                                    </p>
                                </div>
                            </div>
                        </div>
                    )}

                    {section === SECTIONS.privacy && (
                        <div>
                            {backButton}
                            <div className="space-y-4 text-sm text-gray-700">
                                <div className="flex gap-3">
                                    <Database className="w-5 h-5 text-purple-600 shrink-0 mt-0.5" />
                                    <div>
                                        <p className="font-medium text-gray-900">What we store</p>
                                        <p className="mt-1">
                                            Your recordings, documents, tasks, projects, and chat threads are stored in
                                            Firebase under your user ID. Only you can read or change them through the app.
                                        </p>
                                    </div>
                                </div>
                                <div className="flex gap-3">
                                    <Shield className="w-5 h-5 text-purple-600 shrink-0 mt-0.5" />
                                    <div>
                                        <p className="font-medium text-gray-900">AI processing</p>
                                        <p className="mt-1">
                                            Transcription and search use OpenAI and Pinecone. Content is sent only to
                                            generate transcripts, meeting notes, and answers — not sold to third parties.
                                        </p>
                                    </div>
                                </div>
                                <div className="flex gap-3">
                                    <Lock className="w-5 h-5 text-purple-600 shrink-0 mt-0.5" />
                                    <div>
                                        <p className="font-medium text-gray-900">Integrations</p>
                                        <p className="mt-1">
                                            Google and Microsoft tokens are stored server-side. You can disconnect
                                            integrations from the Integrations tab at any time.
                                        </p>
                                    </div>
                                </div>
                                <p className="text-xs text-gray-500 pt-2">
                                    To remove your account data, delete items in the app or contact support. Legacy
                                    knowledge-graph collections can be removed directly in the Firebase console if present.
                                </p>
                            </div>
                        </div>
                    )}

                    {section === SECTIONS.help && (
                        <div>
                            {backButton}
                            <div className="space-y-4 text-sm">
                                <div>
                                    <p className="font-medium text-gray-900">Getting started</p>
                                    <ul className="mt-2 space-y-2 text-gray-600 list-disc list-inside">
                                        <li>Use Knowledge capture to record meetings or run expert interviews.</li>
                                        <li>Upload files under Documents and assign them to projects in Brain.</li>
                                        <li>Ask questions in Knowledge search across your indexed content.</li>
                                    </ul>
                                </div>
                                <div>
                                    <p className="font-medium text-gray-900">Meeting recordings</p>
                                    <p className="mt-1 text-gray-600">
                                        After a recording finishes, open the transcript to see speaker-labelled dialogue,
                                        meeting notes, key topics, and action items.
                                    </p>
                                </div>
                                <div>
                                    <p className="font-medium text-gray-900">Need help?</p>
                                    <p className="mt-1 text-gray-600">
                                        Email your team admin or support contact with your account email and a short
                                        description of the issue.
                                    </p>
                                </div>
                            </div>
                        </div>
                    )}

                    {profileMessage && (
                        <p className="mt-4 text-sm text-gray-700 bg-gray-50 rounded-lg px-3 py-2">{profileMessage}</p>
                    )}
                </div>

                <div className="p-6 border-t border-gray-100">
                    <button
                        type="button"
                        onClick={handleSignOut}
                        className="w-full flex items-center justify-center px-3 py-2.5 bg-red-50 hover:bg-red-100 rounded-xl transition-colors text-red-600 font-medium"
                    >
                        <LogOut className="w-4 h-4 mr-2" />
                        Sign out
                    </button>
                </div>
            </div>
        </div>
    );
};

export default ProfileModal;
